import { Input, List, Spin } from 'antd'
import React, { useState } from 'react'
import { ReactComponent as BayPlanIcon } from 'assets/icons/bay-plan.svg'
import { ReactComponent as ContainerIcon } from 'assets/icons/container.svg'
import filter from 'lodash/filter'
import Icon from '@ant-design/icons'
import orderBy from 'lodash/orderBy'
import PropTypes from 'prop-types'
import Text from 'components/Text'
import { timeDateAndYear } from 'constants/time'
import Timestamp from 'components/Timestamp'
import { useTranslation } from 'react-i18next'

import './containerNotes.less'
import Dropdown from '../Dropdown'

const SHOW_ALL_STATUS = 'SHOW_ALL_STATUS'

const SORT_OPTIONS = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

const STATUS_OPTIONS = {
  ALL: 'ALL',
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
}

const ContainerNotes = ({ isTimestampUTC, isLoading, notes }) => {
  const { t } = useTranslation()

  const { Item } = List

  const [statusFilterBy, setStatusFilterBy] = useState(SHOW_ALL_STATUS)
  const [searchText, setSearchText] = useState('')
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.DESCENDING)

  const onStatusFilterChange = statusInArray =>
    setStatusFilterBy(...statusInArray)

  const onSortByChange = sortByInArray => setSortBy(...sortByInArray)

  const onSearchChange = e => setSearchText(e.target.value)

  const statusOptions = [
    {
      label: <Text>{t('portCall.containerNotebook.showAll')}</Text>,
      value: SHOW_ALL_STATUS,
    },
    {
      label: <Text>{t('portCall.containerNotebook.open')}</Text>,
      value: STATUS_OPTIONS.OPEN,
    },
    {
      label: <Text>{t('portCall.containerNotebook.closed')}</Text>,
      value: STATUS_OPTIONS.CLOSED,
    },
  ]

  const sortByOptions = [
    {
      label: <Text>{t('portCall.logbook.newestFirst')}</Text>,
      value: SORT_OPTIONS.DESCENDING,
    },
    {
      label: <Text>{t('portCall.logbook.oldestFirst')}</Text>,
      value: SORT_OPTIONS.ASCENDING,
    },
  ]

  // Filter container notes by status
  let filteredNotes = notes

  if (statusFilterBy === STATUS_OPTIONS.OPEN) {
    filteredNotes = filter(filteredNotes, note => !note.isComplete)
  } else if (statusFilterBy === STATUS_OPTIONS.CLOSED) {
    filteredNotes = filter(filteredNotes, note => note.isComplete)
  }

  // Filter by search text (containerNo or cellNumber)
  if (searchText) {
    const searchLower = searchText.toLowerCase()

    filteredNotes = filter(filteredNotes, note => {
      const containerNo = note.containerNo ? note.containerNo.toLowerCase() : ''
      const cellNumber = note.cellNumber ? note.cellNumber.toLowerCase() : ''

      return (
        containerNo.includes(searchLower) || cellNumber.includes(searchLower)
      )
    })
  }

  const notesListToRender = orderBy(
    filteredNotes,
    note => new Date(note.loggedAt),
    sortBy
  )

  const renderListItem = item => (
    <Item className="container-notes__list-item">
      <div className="container-notes__list-item-container">
        <div className="container-notes__list-item-header">
          {item.containerNo && (
            <div className="container-notes__container-number">
              <Icon component={ContainerIcon} />
              <Text isBold>{item.containerNo}</Text>
            </div>
          )}
          {item.cellNumber && (
            <div className="container-notes__cell-number">
              <Icon component={BayPlanIcon} />
              <Text isBold>{item.cellNumber}</Text>
            </div>
          )}
          {item.isComplete && (
            <div className="container-notes__status-badge container-notes__status-badge--closed">
              <Text isBold>{t('portCall.containerNotebook.closed')}</Text>
            </div>
          )}
          {!item.isComplete && (
            <div className="container-notes__status-badge container-notes__status-badge--open">
              <Text isBold>{t('portCall.containerNotebook.open')}</Text>
            </div>
          )}
        </div>
        <Text>{item.remark}</Text>
        {/* {item.image && item.image.thumbnailUrl && (
          <div className="container-notes__image-container">
            <img
              alt={t('portCall.containerNotebook.imageAlt')}
              className="container-notes__thumbnail"
              src={item.image.thumbnailUrl}
            />
          </div>
        )} */}
        <div className="container-notes__timestamps">
          <Timestamp
            date={item.loggedAt}
            dateFormat={timeDateAndYear}
            isLabel
            isUTC={isTimestampUTC}
          />
          {/* {item.completedAt && (
            <Text isLabel>
              {t('portCall.containerNotebook.completed')}{' '}
              <Timestamp
                date={item.completedAt}
                dateFormat={timeDateAndYear}
                isUTC={isTimestampUTC}
              />
            </Text>
          )} */}
        </div>
      </div>
    </Item>
  )

  return (
    <div className="container-notes__container">
      <div className="container-notes__search-container">
        <Input
          allowClear
          className="container-notes__search"
          onChange={onSearchChange}
          placeholder={t('portCall.containerNotebook.searchPlaceholder')}
          value={searchText}
        />
      </div>
      <div className="container-notes__dropdown-container">
        <Dropdown
          isDisabled={isLoading}
          label={t('portCall.containerNotebook.status')}
          onChange={onStatusFilterChange}
          options={statusOptions}
          value={statusFilterBy}
        />

        <Dropdown
          isDisabled={isLoading}
          label={t('portCall.logbook.sort')}
          onChange={onSortByChange}
          options={sortByOptions}
          value={sortBy}
        />
      </div>
      {isLoading ? (
        <Spin className="container-notes__spin" />
      ) : (
        <List
          className="container-notes__list"
          dataSource={notesListToRender}
          renderItem={renderListItem}
        />
      )}
    </div>
  )
}

ContainerNotes.defaultProps = {
  isTimestampUTC: false,
  notes: [],
}

ContainerNotes.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isTimestampUTC: PropTypes.bool,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      cellNumber: PropTypes.string,
      completedAt: PropTypes.string,
      containerNo: PropTypes.string,
      image: PropTypes.shape({
        fullUrl: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
      isComplete: PropTypes.bool,
      loggedAt: PropTypes.string.isRequired,
      remark: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
    })
  ),
}

export default ContainerNotes
